import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Wand2, Loader2, Download, Lock, Star, ArrowRight, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { getGallery, adminGallery, adminFeatureGallery, rebuildPdfBlob, scoreTone } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import RebuildGateDialog from "@/components/RebuildGateDialog";
import { toast } from "sonner";

export default function RedesignGallery() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState(null);
  const [gate, setGate] = useState(null); // active concept {id, domain} for the unlock dialog

  const load = async () => {
    try {
      const data = isAdmin ? await adminGallery() : await getGallery();
      setItems(data.items || []);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => { if (user !== undefined) load(); /* eslint-disable-next-line */ }, [user]);

  const openPdf = async (id, token) => {
    try {
      const url = await rebuildPdfBlob(id, user ? {} : { token });
      const win = window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      if (!win) toast.error("Please allow pop-ups to open the report.");
    } catch {
      toast.error("Could not open the PDF. Please try again.");
    }
  };

  const onDownload = (item) => {
    if (user) return openPdf(item.id); // logged-in via auth header
    setGate(item);
  };

  const toggleFeature = async (item) => {
    try {
      await adminFeatureGallery(item.id, !item.featured);
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, featured: !i.featured } : i)));
      toast.success(item.featured ? "Removed from showcase" : "Added to showcase");
    } catch {
      toast.error("Could not update");
    }
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-slate-50">
        {/* Hero */}
        <section className="bg-gradient-to-b from-rose-50 to-white text-slate-900 pt-32 pb-16 md:pt-40 md:pb-20 border-b border-rose-100/70">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={16} className="text-rose-700" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-rose-700">AI Rebuild Gallery</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold max-w-3xl leading-[1.05]">
              Real websites, reimagined by AI
            </h1>
            <p className="text-slate-600 text-base md:text-lg mt-5 max-w-2xl">
              Every concept below was generated from a live website analysis — a fresh hero, palette, sections and conversion strategy. Run your own site and get yours in seconds.
            </p>
            <Link to="/#analyzer" data-testid="gallery-analyze-cta" className="inline-flex items-center gap-2 bg-slate-900 text-white font-semibold rounded-xl px-6 py-3.5 mt-8 hover:-translate-y-0.5 transition-transform">
              <Wand2 size={17} /> Reimagine my website <ArrowRight size={16} />
            </Link>
          </div>
        </section>

        {/* Grid */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          {items === null ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-brand" size={30} /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-slate-500">No redesigns yet. Be the first — analyze your website.</p>
              <Link to="/#analyzer" className="text-brand font-semibold mt-3 inline-block">Analyze a website →</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gallery-grid">
              {items.map((item, idx) => (
                <GalleryCard key={item.id} item={item} idx={idx} isAdmin={isAdmin} authed={!!user} onDownload={onDownload} onToggleFeature={toggleFeature} />
              ))}
            </div>
          )}
        </section>
      </main>

      <RebuildGateDialog
        open={!!gate}
        onOpenChange={(o) => !o && setGate(null)}
        conceptId={gate?.id}
        domain={gate?.domain}
        onUnlocked={({ token }) => { const id = gate?.id; setGate(null); openPdf(id, token); }}
      />
      <Footer />
    </>
  );
}

function GalleryCard({ item, idx, isAdmin, authed, onDownload, onToggleFeature }) {
  const tone = scoreTone(item.overall_score ?? 0);
  const palette = item.palette || [];
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.4, delay: (idx % 3) * 0.08 }}
      className="bg-white border border-slate-200 rounded-2xl card-shadow overflow-hidden flex flex-col group"
      data-testid="gallery-card"
    >
      {/* Palette banner */}
      <div className="h-28 flex relative">
        {palette.length ? palette.map((p, i) => (
          <div key={i} className="flex-1 transition-all group-hover:flex-[1.2]" style={{ background: p.hex }} />
        )) : <div className="flex-1 bg-slate-800" />}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="text-[10px] font-mono bg-black/50 text-white rounded px-2 py-1 backdrop-blur-sm">{item.domain}</span>
        </div>
        {item.featured && !isAdmin && (
          <span className="absolute top-3 right-3 flex items-center gap-1 text-[10px] font-semibold bg-white/90 text-amber-600 rounded-full px-2 py-1">
            <Star size={11} className="fill-amber-500 text-amber-500" /> Featured
          </span>
        )}
      </div>

      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-heading text-lg font-bold text-slate-900 leading-snug">{item.headline || "Modern redesign concept"}</h3>
          {item.overall_score != null && (
            <div className="text-right shrink-0">
              <span className="font-heading text-2xl font-extrabold" style={{ color: tone.color }}>{item.overall_score}</span>
              <p className="text-[9px] uppercase tracking-wide text-slate-400 -mt-1">before</p>
            </div>
          )}
        </div>
        {item.subheadline && <p className="text-sm text-slate-500 mt-2 line-clamp-2">{item.subheadline}</p>}
        {item.style_direction && <p className="text-xs text-slate-400 mt-3 line-clamp-2 italic">{item.style_direction}</p>}

        <div className="flex flex-wrap gap-1.5 mt-4">
          {palette.slice(0, 5).map((p, i) => (
            <span key={i} className="text-[9px] font-mono text-slate-400 border border-slate-100 rounded px-1.5 py-0.5">{p.hex}</span>
          ))}
        </div>

        <div className="mt-auto pt-5 flex items-center gap-2">
          <button onClick={() => onDownload(item)} data-testid="gallery-download-btn"
            className="flex-1 bg-gradient-to-br from-[#5C162E] to-[#3D0E1E] text-white text-sm font-semibold rounded-xl px-4 py-2.5 flex items-center justify-center gap-2 hover:bg-slate-800 transition-colors">
            {authed ? <Download size={13} /> : <Lock size={13} />} {authed ? "Download PDF" : "View concept PDF"}
          </button>
          {isAdmin && (
            <button onClick={() => onToggleFeature(item)} data-testid="gallery-feature-toggle"
              title={item.featured ? "Remove from showcase" : "Add to showcase"}
              className={`w-10 h-10 shrink-0 rounded-xl flex items-center justify-center border transition-colors ${item.featured ? "bg-amber-50 border-amber-200 text-amber-500" : "border-slate-200 text-slate-400 hover:text-amber-500"}`}>
              <Star size={16} className={item.featured ? "fill-amber-500" : ""} />
            </button>
          )}
        </div>
        {isAdmin && item.unlocked && (
          <p className="text-[10px] text-emerald-600 mt-2 truncate">Lead: {item.lead_email}</p>
        )}
      </div>
    </motion.div>
  );
}
